import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	Table,
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	Row,
	Col
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import ingredientService from "../../services/ingredientService";
import { IApplicationState } from "../../redux/reducers";
import { ingredients as ingredientConstants } from "../../redux/actions/actionContants";
import { FaCheckCircle, FaChartLine, FaGlobe, FaExclamationTriangle } from "react-icons/fa";
import { USER_ROLES } from "../../interfaces/roles";
import AIAgentBox from "../../components/common/AIAgentBox";
import AIAnalysisResultsModal from "../../components/modals/AIAnalysisResultsModal";

// Prefer importing Bootstrap once at app entry; keeping here if you rely on it locally.
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/scss/pages/admin/_ingredientsAdmin.scss";

const IngredientsTable: React.FC = () => {
	const dispatch = useDispatch<any>();

	// Redux state
	const ingredients = useSelector(
		(state: IApplicationState) => state.ingredient?.ingredients?.list ?? []
	);
	const pagination = useSelector(
		(state: IApplicationState) => state.ingredient?.ingredients?.pagination
	);
	const currentPage = pagination?.page || 1;
	const totalPages = pagination?.pages || 1;

	// Local UI state
	const [listLoading, setListLoading] = useState<boolean>(true);
	const [hydrated, setHydrated] = useState<boolean>(false);
	const [actionLoading, setActionLoading] = useState<boolean>(false);
	const [search, setSearch] = useState("");

	const [editModalOpen, setEditModalOpen] = useState(false);
	const [editLoading, setEditLoading] = useState(false);
	const [isAIAnalysisModalOpen, setIsAIAnalysisModalOpen] = useState(false);
	const [editData, setEditData] = useState<any>({});
	const [formData, setFormData] = useState<any>({
		name: "",
		category: "",
		waterUsage: "",
		greenhouseEmission: "",
		recommendationLevel: "MEDIUM",
		verified: false,
		globalAvailability: false
	});
	const [pendingPage, setPendingPage] = useState<number | null>(null);

	const role = useSelector(
		(state: IApplicationState) =>
			(state as any)?.user?.PROFILE_RESPONSE?.role || (state as any)?.user?.role
	);
	const canDelete = role === USER_ROLES.COMPANY_ADMIN;

	// Hydration snapshot
	const snapRef = useRef({
		listRef: ingredients as any[],
		listLen: (ingredients as any[]).length,
		page: currentPage,
		pages: totalPages
	});

	const markSnapshot = () => {
		snapRef.current = {
			listRef: ingredients as any[],
			listLen: (ingredients as any[]).length,
			page: currentPage,
			pages: totalPages
		};
	};

	const detectHydration = () => {
		const s = snapRef.current;
		const changed =
			ingredients !== s.listRef ||
			(ingredients?.length ?? 0) !== (s.listLen ?? 0) ||
			totalPages !== s.pages;

		if (changed) {
			if (!hydrated) setHydrated(true);
			if (listLoading) setListLoading(false);
		}
	};

	// Search filter
	const filteredIngredients = useMemo(() => {
		const q = search.toLowerCase();
		return (ingredients as any[]).filter((i) => (i?.name ?? "").toLowerCase().includes(q));
	}, [ingredients, search]);

	// Fetch list (no await; detect via store change)
	useEffect(() => {
		markSnapshot();
		setListLoading(true);
		dispatch(ingredientService.getPaginatedIngredients());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, currentPage]);

	useEffect(() => {
		detectHydration();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ingredients, totalPages]);

	// Edit: open immediately from row; optional background refresh
	const handleEdit = (row: any) => {
		setEditData(row);
		setFormData({
			name: row?.name || "",
			category: row?.category || "",
			waterUsage: row?.waterUsage || "",
			greenhouseEmission: row?.greenhouseEmission || "",
			recommendationLevel: row?.recommendationLevel || "MEDIUM",
			verified: !!row?.verified,
			globalAvailability: !!row?.globalAvailability
		});

		setEditModalOpen(true);

		if (row?.id) {
			setEditLoading(true);
			dispatch(ingredientService.getIngredientById(String(row.id)))
				.then((res: any) => {
					const d = res?.payload ?? res;
					if (d) {
						setEditData(d);
						setFormData((prev: any) => ({
							...prev,
							name: d?.name ?? prev.name,
							category: d?.category ?? prev.category,
							waterUsage: d?.waterUsage ?? prev.waterUsage,
							greenhouseEmission: d?.greenhouseEmission ?? prev.greenhouseEmission,
							recommendationLevel: d?.recommendationLevel ?? prev.recommendationLevel,
							verified: typeof d?.verified === "boolean" ? d.verified : prev.verified,
							globalAvailability:
								typeof d?.globalAvailability === "boolean"
									? d.globalAvailability
									: prev.globalAvailability
						}));
					}
				})
				.catch((e: any) => console.error("Edit refresh failed", e))
				.finally(() => setEditLoading(false));
		}
	};

	const handleDelete = async (ingredientId: string) => {
		if (!window.confirm("Are you sure you want to delete this ingredient?")) return;

		setActionLoading(true);
		try {
			await dispatch(ingredientService.removeIngredient(ingredientId));
			markSnapshot();
			setListLoading(true);
			dispatch(ingredientService.getPaginatedIngredients());
		} catch (error) {
			console.error("Failed to delete ingredient", error);
		} finally {
			setActionLoading(false);
		}
	};

	const handleSaveEdit = async () => {
		setActionLoading(true);
		try {
			await dispatch(ingredientService.editIngredient(editData.id, formData));
			setEditModalOpen(false);
			markSnapshot();
			setListLoading(true);
			dispatch(ingredientService.getPaginatedIngredients());
		} catch (error) {
			console.error("Failed to update ingredient", error);
		} finally {
			setActionLoading(false);
		}
	};

	const goToPage = (page: number) => {
		if (page < 1 || page > totalPages || page === currentPage || listLoading) return;
		setPendingPage(page); // mark which page was clicked
		dispatch({ type: ingredientConstants.PAGINATE_INGREDIENTS, payload: page });
	};
	useEffect(() => {
		// when loading finishes and store reflects the page, clear pending
		if (!listLoading && pendingPage != null && pendingPage === currentPage) {
			setPendingPage(null);
		}
	}, [listLoading, currentPage, pendingPage]);

	return (
		<div className="container-fluid">
			{/* AI Agent Box */}
			<AIAgentBox
				title="AI Ingredients Agent"
				description="Analyze ingredient data completeness across nutrition, pricing, and sustainability metrics with automated enrichment from trusted sources"
				features={[
					{ icon: <FaCheckCircle />, text: "Nutrition data validation", iconColor: "#28a745" },
					{ icon: <FaChartLine />, text: "Price trend analysis", iconColor: "#0d6efd" },
					{ icon: <FaGlobe />, text: "Sustainability metrics", iconColor: "#17a2b8" },
					{
						icon: <FaExclamationTriangle />,
						text: "Flag high-impact ingredients",
						iconColor: "#dc3545"
					}
				]}
				theme="green"
				onButtonClick={() => setIsAIAnalysisModalOpen(true)}
			/>

			{/* Search */}
			<div className="admin-data-search">
				<div className="admin-data-search__input-wrapper">
					<input
						type="text"
						placeholder="Search ingredients by name, category, supplier, or status..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="admin-data-search__input"
						aria-label="Search ingredients"
					/>
					<i className="fas fa-search admin-data-search__icon"></i>
					{search && (
						<button
							type="button"
							className="admin-data-search__clear-btn"
							onClick={() => setSearch("")}
							aria-label="Clear search"
						>
							<i className="fas fa-times"></i>
						</button>
					)}
				</div>
				<div className="admin-data-search__actions">
					<button
						type="button"
						className="admin-data-search__button btn-primary"
						disabled={actionLoading}
					>
						<i className="fas fa-plus"></i>
						Add Ingredient
					</button>
				</div>
			</div>

			{/* Table */}
			<div className="admin-data-table-wrapper">
				<div className="table-responsive">
					<Table className="admin-data-table ingredients-table">
						<thead>
							<tr className="table-header">
								<th>Ingredient</th>
								<th>Category</th>
								<th>Supplier</th>
								<th>Status</th>
								<th>Allergens</th>
								<th>Actions</th>
							</tr>
						</thead>
						<tbody>
							{!hydrated || listLoading ? (
								<tr className="loading-row">
									<td colSpan={6} className="loading-cell">
										<div className="loading-spinner">
											<div className="spinner"></div>
											<span>Loading ingredients...</span>
										</div>
									</td>
								</tr>
							) : filteredIngredients.length === 0 ? (
								<tr>
									<td colSpan={6} className="empty-cell">
										<div className="empty-state">
											<div className="empty-icon">🥬</div>
											<span>
												{search ? "No ingredients match your search" : "No ingredients found"}
											</span>
										</div>
									</td>
								</tr>
							) : (
								filteredIngredients.map((ingredient: any) => (
									<tr key={ingredient.id} className="table-row clickable">
										<td className="cell-name" title={ingredient.name || "No Name"}>
											<strong>{ingredient.name || "No Name"}</strong>
										</td>
										<td title={ingredient.category || "No Category"}>
											{ingredient.category || "No Category"}
										</td>
										<td title={ingredient.supplier || "No Supplier"}>
											{ingredient.supplier || "No Supplier"}
										</td>
										<td className="cell-status">
											<span
												className={`status-badge status-${(
													ingredient.status || "active"
												).toLowerCase()}`}
											>
												{ingredient.status || "Active"}
											</span>
										</td>
										<td>
											{Array.isArray(ingredient.allergens) && ingredient.allergens.length ? (
												ingredient.allergens.map((a: any) => (
													<span key={a.allergen} className="allergen-badge" title={a.allergen}>
														{a.allergen}
													</span>
												))
											) : (
												<span className="allergen-badge none">None</span>
											)}
										</td>
										<td className="actions-cell">
											<div className="btn-group">
												<button
													type="button"
													className="action-btn btn-edit"
													onClick={() => handleEdit(ingredient)}
													disabled={actionLoading}
												>
													Edit
												</button>
												{canDelete && (
													<button
														type="button"
														className="action-btn btn-delete"
														onClick={() => handleDelete(String(ingredient.id))}
														disabled={actionLoading}
													>
														Delete
													</button>
												)}
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</Table>
				</div>
			</div>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="admin-data-pagination">
					<div className="admin-data-pagination__controls">
						<button
							type="button"
							className="admin-data-pagination__button btn-prev"
							disabled={currentPage === 1 || listLoading}
							onClick={() => goToPage(currentPage - 1)}
						>
							<i className="fas fa-chevron-left"></i>
							Previous
						</button>
					</div>
					<div className="admin-data-pagination__info">
						<div className="page-info">
							Page {currentPage} of {totalPages}
						</div>
						{search && <div className="results-info">{filteredIngredients.length} results</div>}
					</div>
					<div className="admin-data-pagination__next-controls">
						<button
							type="button"
							className="admin-data-pagination__button btn-next"
							disabled={currentPage === totalPages || listLoading}
							onClick={() => goToPage(currentPage + 1)}
						>
							Next
							<i className="fas fa-chevron-right"></i>
						</button>
					</div>
				</div>
			)}

			{/* Edit Modal */}
			<Modal
				isOpen={editModalOpen}
				toggle={() => setEditModalOpen(false)}
				centered
				size="lg"
				modalClassName="ingredients-modal"
				contentClassName="ingredients-modal__content"
			>
				<ModalHeader toggle={() => setEditModalOpen(false)}>Edit Ingredient</ModalHeader>
				<ModalBody>
					{editLoading ? (
						<div className="loading-cell" style={{ padding: 16 }}>
							Loading...
						</div>
					) : (
						<Row className="g-3">
							<Col md={6}>
								<label>Ingredient Name</label>
								<Input
									value={formData.name ?? ""}
									onChange={(e) => setFormData({ ...formData, name: e.target.value })}
									className="form-input"
								/>
							</Col>
							<Col md={6}>
								<label>Ingredient Category</label>
								<Input
									value={formData.category ?? ""}
									onChange={(e) => setFormData({ ...formData, category: e.target.value })}
									className="form-input"
								/>
							</Col>
							<Col md={6}>
								<label>Water Usage</label>
								<Input
									value={formData.waterUsage ?? ""}
									onChange={(e) => setFormData({ ...formData, waterUsage: e.target.value })}
									className="form-input"
								/>
							</Col>
							<Col md={6}>
								<label>Greenhouse Emission</label>
								<Input
									value={formData.greenhouseEmission ?? ""}
									onChange={(e) => setFormData({ ...formData, greenhouseEmission: e.target.value })}
									className="form-input"
								/>
							</Col>
							<Col md={6}>
								<label>Recommendation Priority Level</label>
								<Input
									type="select"
									value={formData.recommendationLevel ?? "MEDIUM"}
									onChange={(e) =>
										setFormData({ ...formData, recommendationLevel: e.target.value })
									}
									className="form-select-input"
								>
									<option value="HIGH">HIGH</option>
									<option value="MEDIUM">MEDIUM</option>
									<option value="LOW">LOW</option>
								</Input>
							</Col>

							<Col md={3}>
								<label>Verified</label>
								<div className="toggle-button-group">
									<button
										type="button"
										className={`toggle-btn ${formData.verified ? "active" : ""}`}
										onClick={() => setFormData({ ...formData, verified: true })}
										disabled={actionLoading}
									>
										Yes
									</button>
									<button
										type="button"
										className={`toggle-btn ${!formData.verified ? "active" : ""}`}
										onClick={() => setFormData({ ...formData, verified: false })}
										disabled={actionLoading}
									>
										No
									</button>
								</div>
							</Col>

							<Col md={3}>
								<label>Global Availability</label>
								<div className="toggle-button-group">
									<button
										type="button"
										className={`toggle-btn ${formData.globalAvailability ? "active" : ""}`}
										onClick={() => setFormData({ ...formData, globalAvailability: true })}
										disabled={actionLoading}
									>
										Yes
									</button>
									<button
										type="button"
										className={`toggle-btn ${!formData.globalAvailability ? "active" : ""}`}
										onClick={() => setFormData({ ...formData, globalAvailability: false })}
										disabled={actionLoading}
									>
										No
									</button>
								</div>
							</Col>
						</Row>
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="primary" onClick={handleSaveEdit} disabled={actionLoading || editLoading}>
						{actionLoading ? "Saving..." : "Save"}
					</Button>
					<Button
						color="secondary"
						onClick={() => setEditModalOpen(false)}
						disabled={actionLoading}
					>
						Cancel
					</Button>
				</ModalFooter>
			</Modal>

			{/* AI Analysis Results Modal */}
			<AIAnalysisResultsModal
				isOpen={isAIAnalysisModalOpen}
				toggle={() => setIsAIAnalysisModalOpen(false)}
				collectionName="ingredient_list"
				collectionLabel="Ingredients"
				onAnalysisComplete={() => {
					// Refresh ingredients list after analysis is applied
					setListLoading(true);
					dispatch(ingredientService.getPaginatedIngredients());
				}}
			/>
		</div>
	);
};

export default IngredientsTable;
